import React, { useEffect, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";
import { Plus, Pencil, Trash2, X, Upload, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const emptyProduct = () => ({
  slug: "", name: "", brand: "", category_slug: "ott", short_description: "", description: "",
  image_url: "", gallery: [], features: [], plans: [
    { label: "1 Month", duration_months: 1, price: 299, original_price: 0, badge: "" },
    { label: "3 Months", duration_months: 3, price: 799, original_price: 0, badge: "" },
    { label: "6 Months", duration_months: 6, price: 1499, original_price: 0, badge: "Popular" },
    { label: "12 Months", duration_months: 12, price: 2499, original_price: 0, badge: "Best Value" },
  ],
  is_featured: false, is_trending: false, is_bestseller: false, is_active: true,
  rating: 4.7, review_count: 0,
});

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const [{ data: p }, { data: c }] = await Promise.all([
      api.get("/products", { params: { active_only: false, limit: 500 } }),
      api.get("/categories"),
    ]);
    setProducts(p); setCategories(c);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing(emptyProduct());
  const save = async () => {
    try {
      if (!editing.name || !editing.slug) { toast.error("Name and slug are required"); return; }
      if (!editing.plans?.length) { toast.error("At least one pricing variant required"); return; }
      if (editing.id) {
        await api.put(`/products/${editing.id}`, editing);
        toast.success("Product updated");
      } else {
        await api.post("/products", editing);
        toast.success("Product created");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted"); load();
  };

  const uploadImage = async (file, target = "main") => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = getToken();
      const res = await fetch(`${API_BASE}/uploads/image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        body: fd,
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      if (target === "main") {
        setEditing((p) => ({ ...p, image_url: fullUrl }));
      } else {
        setEditing((p) => ({ ...p, gallery: [...(p.gallery || []), fullUrl] }));
      }
      toast.success("Image uploaded");
    } catch (e) { toast.error("Upload failed — check EMERGENT_LLM_KEY in backend .env"); }
    finally { setUploading(false); }
  };

  const addPlan = () => {
    setEditing({
      ...editing,
      plans: [...editing.plans, { label: "New variant", duration_months: 1, price: 0, original_price: 0, badge: "" }],
    });
  };
  const removePlan = (i) => {
    const p = [...editing.plans]; p.splice(i, 1);
    setEditing({ ...editing, plans: p });
  };
  const updatePlan = (i, patch) => {
    const p = [...editing.plans]; p[i] = { ...p[i], ...patch };
    setEditing({ ...editing, plans: p });
  };

  const filtered = products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="kyro-overline">Catalog</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-2">Products</h1>
        </div>
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none" data-testid="admin-product-search" />
          <button onClick={startNew} data-testid="admin-new-product-btn" className="kyro-btn-primary text-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" /> New product</button>
        </div>
      </div>

      <div className="kyro-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium text-kyro-mute">Product</th>
              <th className="px-4 py-3 font-medium text-kyro-mute">Category</th>
              <th className="px-4 py-3 font-medium text-kyro-mute">Variants</th>
              <th className="px-4 py-3 font-medium text-kyro-mute">From</th>
              <th className="px-4 py-3 font-medium text-kyro-mute">Status</th>
              <th className="px-4 py-3 font-medium text-kyro-mute w-0"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-white/5" data-testid={`admin-product-row-${p.slug}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-white/5 overflow-hidden shrink-0">
                      {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <p className="font-semibold">{p.name}</p>
                      <p className="text-xs text-kyro-mute">{p.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-kyro-mute">{p.category_slug}</td>
                <td className="px-4 py-3 text-kyro-mute">{p.plans?.length || 0}</td>
                <td className="px-4 py-3">₹{p.plans?.[0]?.price?.toFixed(0)}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold uppercase ${p.is_active ? "text-emerald-400" : "text-kyro-mute"}`}>{p.is_active ? "Active" : "Hidden"}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={() => setEditing({ ...p })} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white/5" data-testid={`edit-product-${p.slug}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(p.id)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white/5 text-red-400" data-testid={`delete-product-${p.slug}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm grid place-items-start p-4 overflow-y-auto">
          <div className="kyro-card w-full max-w-4xl p-6 md:p-8 my-8 mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="kyro-overline">{editing.id ? "Edit product" : "New product"}</div>
                <h2 className="font-display font-bold text-2xl mt-1">{editing.name || "Untitled"}</h2>
              </div>
              <button onClick={() => setEditing(null)} className="w-9 h-9 grid place-items-center rounded-full hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>

            {/* SECTION 1: MEDIA */}
            <div className="mb-8">
              <div className="kyro-overline mb-3">Product image</div>
              <div className="grid md:grid-cols-[220px_1fr] gap-4">
                <div className="relative">
                  <div className="w-full aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10 grid place-items-center">
                    {editing.image_url ? (
                      <img src={editing.image_url} alt="preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center p-4">
                        <ImagePlus className="w-6 h-6 mx-auto text-kyro-mute mb-2" />
                        <p className="text-xs text-kyro-mute">No image yet</p>
                      </div>
                    )}
                  </div>
                  {editing.image_url && (
                    <button onClick={() => setEditing({ ...editing, image_url: "" })} className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full bg-black/70 hover:bg-red-500/80 text-white" title="Remove"><X className="w-3.5 h-3.5" /></button>
                  )}
                </div>
                <div className="space-y-3">
                  <label className="kyro-btn-primary text-sm cursor-pointer inline-flex items-center gap-2">
                    <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload from your device"}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], "main")} data-testid="prod-image-upload" />
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-xs text-kyro-faint">or paste URL</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <input placeholder="https://…" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433] text-sm" data-testid="prod-image-url" />
                  <p className="text-xs text-kyro-faint">Recommended: 800×600px, JPG/PNG/WebP.</p>
                </div>
              </div>
            </div>

            {/* SECTION 2: BASIC INFO */}
            <div className="mb-8">
              <div className="kyro-overline mb-3">Basic info</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Name *</label>
                  <input placeholder="Netflix Premium 4K" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value, slug: editing.id ? editing.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" data-testid="prod-name" />
                </div>
                <div>
                  <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Slug (URL) *</label>
                  <input placeholder="netflix-premium" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" data-testid="prod-slug" />
                </div>
                <div>
                  <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Brand</label>
                  <input placeholder="Netflix" value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" />
                </div>
                <div>
                  <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Category</label>
                  <select value={editing.category_slug} onChange={(e) => setEditing({ ...editing, category_slug: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none">
                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Short description (card)</label>
                  <input placeholder="4K UHD streaming. Watch on 4 screens at once." value={editing.short_description} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Full description (product page)</label>
                  <textarea placeholder="Enjoy Netflix's entire catalog in 4K UHD..." value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={4} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] text-kyro-mute uppercase tracking-widest mb-1 block">Features (comma separated)</label>
                  <input placeholder="4K UHD + HDR, 4 screens, Downloads, Instant delivery" value={editing.features.join(", ")} onChange={(e) => setEditing({ ...editing, features: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" />
                </div>
              </div>
            </div>

            {/* SECTION 3: PRICING VARIANTS */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <div className="kyro-overline">Pricing variants — {editing.plans.length}</div>
                <button onClick={addPlan} className="kyro-btn-secondary text-xs inline-flex items-center gap-1" data-testid="add-variant-btn"><Plus className="w-3.5 h-3.5" /> Add variant</button>
              </div>
              <p className="text-xs text-kyro-mute mb-4">Each variant can have its own price, duration, discount and badge. Customers pick one before adding to cart.</p>

              <div className="space-y-3">
                {editing.plans.map((pl, i) => (
                  <div key={i} className="kyro-card p-4 md:p-5 border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-xs font-bold uppercase tracking-widest text-[#FF4433]">Variant {i + 1}</div>
                      <button onClick={() => removePlan(i)} className="w-7 h-7 grid place-items-center rounded-full hover:bg-red-500/10 text-red-400" title="Remove variant" data-testid={`remove-variant-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="md:col-span-2">
                        <label className="text-[10px] text-kyro-mute uppercase tracking-widest mb-1 block">Label *</label>
                        <input placeholder="1 Month" value={pl.label} onChange={(e) => updatePlan(i, { label: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF4433]" data-testid={`variant-label-${i}`} />
                      </div>
                      <div>
                        <label className="text-[10px] text-kyro-mute uppercase tracking-widest mb-1 block">Months</label>
                        <input placeholder="1" type="number" min="0" value={pl.duration_months} onChange={(e) => updatePlan(i, { duration_months: parseInt(e.target.value || "0") })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF4433]" data-testid={`variant-months-${i}`} />
                      </div>
                      <div>
                        <label className="text-[10px] text-kyro-mute uppercase tracking-widest mb-1 block">Price ₹ *</label>
                        <input placeholder="299" type="number" min="0" step="1" value={pl.price} onChange={(e) => updatePlan(i, { price: parseFloat(e.target.value || "0") })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF4433] font-semibold" data-testid={`variant-price-${i}`} />
                      </div>
                      <div>
                        <label className="text-[10px] text-kyro-mute uppercase tracking-widest mb-1 block">MRP (strike)</label>
                        <input placeholder="Optional" type="number" min="0" value={pl.original_price ?? 0} onChange={(e) => updatePlan(i, { original_price: parseFloat(e.target.value || "0") })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF4433]" />
                      </div>
                      <div className="col-span-2 md:col-span-4">
                        <label className="text-[10px] text-kyro-mute uppercase tracking-widest mb-1 block">Badge (optional, e.g. "Best Value")</label>
                        <input placeholder="Popular / Best Value / Save 40%" value={pl.badge || ""} onChange={(e) => updatePlan(i, { badge: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF4433]" />
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        {pl.original_price > pl.price && pl.original_price > 0 && (
                          <div className="text-xs text-emerald-400 font-bold">Save {Math.round(((pl.original_price - pl.price) / pl.original_price) * 100)}%</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {editing.plans.length === 0 && (
                <div className="kyro-card p-8 text-center text-kyro-mute">
                  No variants yet. Add at least one.
                </div>
              )}
            </div>

            {/* SECTION 4: FLAGS */}
            <div className="mb-8">
              <div className="kyro-overline mb-3">Visibility & merchandising</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { k: "is_active", label: "Active (visible to customers)" },
                  { k: "is_featured", label: "Featured (homepage)" },
                  { k: "is_trending", label: "Trending" },
                  { k: "is_bestseller", label: "Best Seller" },
                ].map((f) => (
                  <label key={f.k} className={`kyro-card p-3 flex items-center gap-2 text-sm cursor-pointer transition-colors ${editing[f.k] ? "border-[#FF4433]/50 bg-[#FF4433]/5" : "hover:bg-white/5"}`}>
                    <input type="checkbox" checked={editing[f.k]} onChange={(e) => setEditing({ ...editing, [f.k]: e.target.checked })} className="accent-[#FF4433]" data-testid={`prod-${f.k}`} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-white/5 pt-6">
              <button onClick={() => setEditing(null)} className="kyro-btn-secondary text-sm">Cancel</button>
              <button onClick={save} className="kyro-btn-primary text-sm" data-testid="save-product-btn">{editing.id ? "Save changes" : "Create product"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
